import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Shield, Sparkles, Heart, ArrowRight, Star, Users, MapPin, Award } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

// ── Images ────────────────────────────────────────────────────────────────────
// All sourced from Unsplash — swap for your own brand photography when ready

const HERO_IMAGE = "/Storypagehero.png";

const STORY_IMAGES = [
  {
    src: "/ChatGPT Image Jul 1, 2026, 11_08_49 PM.png",
    alt: "Silver jewellery being crafted by artisan hands",
  },
  {
    src: "/ChatGPT Image Jul 1, 2026, 08_15_22 PM.png",
    alt: "Close-up of silver rings",
  },
];

const CRAFT_IMAGE = "/ChatGPT Image Jul 1, 2026, 08_41_26 PM.png";

// ── Stats ─────────────────────────────────────────────────────────────────────

const STATS = [
  { icon: Users,  value: "Loved", label: "by Customers" },
  { icon: Star,   value: "4.9",    label: "Average Rating" },
  { icon: MapPin, value: "Jaipur", label: "Crafted In" },
  { icon: Award,  value: "92.5",   label: "Silver Purity" },
];

// ── Why Rivora pillars ────────────────────────────────────────────────────────

const WHY_RIVORA = [
  {
    icon: Shield,
    title: "Certified 92.5 Silver",
    description: "Every Rivora piece is crafted from certified 92.5 sterling silver — never plated, never compromised. Pure silver made to last.",
  },
  {
    icon: Sparkles,
    title: "Designed for Today",
    description: "Minimal, versatile jewellery inspired by modern women — pieces that move with you, from morning to evening.",
  },
  {
    icon: Heart,
    title: "Crafted by Hand",
    description: "Every Rivora piece is finished by skilled artisans in Jaipur, where jewellery-making is a tradition passed down through generations.",
  },
];

// ── Intersection Observer hook ────────────────────────────────────────────────

function useSectionReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add("section-visible");
          el.classList.remove("section-hidden");
        }
      },
      { threshold: 0.08 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return ref;
}

// ── Main component ────────────────────────────────────────────────────────────

export default function OurStoryPage() {
  const statsRef      = useSectionReveal();
  const storyRef      = useSectionReveal();
  const missionRef    = useSectionReveal();
  const craftRef      = useSectionReveal();
  const whyRef        = useSectionReveal();
  const founderRef    = useSectionReveal();
  const ctaRef        = useSectionReveal();

  return (
    <div className="min-h-screen flex flex-col" data-testid="our-story-page">
      <Navbar />

      {/* ── HERO — full-bleed image with text overlay ─────────────────── */}
      <section
        className="relative min-h-[55vw] sm:min-h-[60vh] lg:min-h-[75vh] flex items-end overflow-hidden"
        style={{ maxHeight: "680px" }}
        data-testid="story-hero"
      >
        {/* Background image — anchor to bottom-center so jewellery stays visible on mobile */}
        <img
          src={HERO_IMAGE}
          alt="Rivora jewellery lifestyle"
          className="absolute inset-0 w-full h-full object-cover object-[center_30%]"
        />

        {/* Dark overlay — left heavy on desktop, full-bottom on mobile for readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-900/90 via-zinc-900/40 to-zinc-900/10 sm:bg-none" />
        <div className="absolute inset-0 hidden sm:block bg-gradient-to-r from-zinc-900/85 via-zinc-900/40 to-zinc-900/10" />

        {/* Text content */}
        <div className="relative z-10 px-6 md:px-16 max-w-7xl mx-auto w-full pb-10 sm:pb-16 md:pb-24">
          <p className="text-[10px] sm:text-xs uppercase tracking-[0.25em] text-[#D4AF37] mb-3 animate-fade-slide-up">
            Jaipur – Est. 2026
          </p>
          <h1
            className="cormorant text-4xl sm:text-6xl lg:text-8xl font-light text-white leading-none tracking-tight animate-fade-slide-up"
            style={{ animationDelay: "100ms" }}
            data-testid="story-heading"
          >
            Our Story
          </h1>
          <p
            className="text-xs sm:text-sm font-light text-white/70 mt-4 max-w-[260px] sm:max-w-xs leading-relaxed animate-fade-slide-up"
            style={{ animationDelay: "200ms" }}
          >
            Crafted in Jaipur.<br />
            Made from certified 925 sterling silver.<br />
            Jewellery designed to become part of your everyday story.
          </p>
          {/* Gold rule */}
          <div
            className="w-10 h-0.5 bg-[#D4AF37] mt-6 animate-fade-slide-up"
            style={{ animationDelay: "300ms" }}
            aria-hidden="true"
          />
        </div>
      </section>

      {/* ── STATS STRIP — dark bg, seamlessly below hero ─────────────────── */}
      <section
        ref={statsRef}
        className="bg-zinc-900 border-t border-zinc-800 section-hidden"
        data-testid="story-stats"
      >
        <div className="px-4 md:px-12 max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-zinc-800 divide-y md:divide-y-0 md:divide-x">
            {STATS.map(({ icon: Icon, value, label }, idx) => (
              <div
                key={label}
                className={`flex flex-col items-center py-6 px-3 text-center ${
                  idx % 2 === 0 ? "border-r border-zinc-800 md:border-r-0" : ""
                }`}
              >
                <Icon size={20} className="text-[#D4AF37] mb-2" strokeWidth={1.5} />
                <p className="cormorant text-2xl sm:text-3xl font-light text-white">{value}</p>
                <p className="text-[9px] sm:text-[10px] uppercase tracking-widest text-zinc-400 mt-1">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── BRAND STORY — asymmetric text + image layout ─────────────────── */}
      <section
        ref={storyRef}
        className="bg-white py-20 sm:py-28 section-hidden"
        data-testid="story-brand-story"
      >
        <div className="px-6 md:px-12 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">

            {/* Text */}
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-4">
                How It Started
              </p>
              {/* Heading with vertical gold accent */}
              <div className="flex gap-4 mb-8">
                <div className="w-0.5 bg-[#C9A96E] flex-shrink-0 rounded-full" aria-hidden="true" />
                <h2 className="cormorant text-4xl sm:text-5xl font-light text-zinc-900 leading-tight">
                  Every Piece Begins<br />with a Story.
                </h2>
              </div>
              <div className="space-y-5 text-zinc-600 text-base" style={{ lineHeight: "1.9" }}>
                <p>
                  Rivora was born from a simple belief — jewellery shouldn't wait for special occasions.
                  It should become part of everyday moments, quietly elevating the way a woman feels every day.
                </p>
                <p>
                  We started in a small studio in Jaipur, surrounded by artisans who had spent
                  decades mastering the craft of silver. We watched, we learned, and we asked one
                  question over and over:
                </p>
                {/* Pull quote */}
                <blockquote className="border-l-2 border-[#C9A96E] pl-4 my-2">
                  <p className="cormorant text-xl sm:text-2xl font-light italic text-zinc-800 leading-snug">
                    "What does the modern Indian woman actually want?"
                  </p>
                </blockquote>
                <p>
                  The answer was always the same — pieces that are real, that last, that go with
                  her everywhere. Not delicate showpieces locked in a locker. Jewellery that lives
                  her life with her.
                </p>
              </div>
            </div>

            {/* Images — staggered overlap, bottom image reduced to ~25% */}
            <div className="relative h-96 lg:h-[520px]">
              <img
                src={STORY_IMAGES[0].src}
                alt={STORY_IMAGES[0].alt}
                className="absolute top-0 left-0 w-3/4 h-3/4 object-cover shadow-xl"
              />
              <img
                src={STORY_IMAGES[1].src}
                alt={STORY_IMAGES[1].alt}
                className="absolute bottom-0 right-0 w-[48%] h-[48%] object-cover shadow-xl border-4 border-white"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── MISSION + VISION — combined, side by side ────────────────────── */}
      <section
        ref={missionRef}
        className="section-hidden py-14 sm:py-20"
        style={{
          background: "linear-gradient(135deg, #FAF7F2 0%, #F5EFE6 50%, #FAF7F2 100%)",
        }}
        data-testid="story-mission-vision"
      >
        <div className="px-6 md:px-12 max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-0 items-start">

            {/* Mission */}
            <div className="text-center px-6 md:px-10 pb-10 md:pb-0" data-testid="story-mission">
              {/* Icon */}
              <div className="flex justify-center mb-4">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#C9A96E" strokeWidth="1.5" className="text-[#C9A96E]">
                  <circle cx="12" cy="12" r="10" />
                  <circle cx="12" cy="12" r="3" />
                  <line x1="12" y1="2" x2="12" y2="5" />
                  <line x1="12" y1="19" x2="12" y2="22" />
                  <line x1="2" y1="12" x2="5" y2="12" />
                  <line x1="19" y1="12" x2="22" y2="12" />
                </svg>
              </div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#C9A96E] mb-4">Our Mission</p>
              <p className="cormorant text-xl sm:text-2xl font-light text-zinc-800" style={{ lineHeight: "1.75" }}>
                To create timeless sterling silver jewellery that combines exceptional craftsmanship with effortless everyday elegance.
              </p>
            </div>

            {/* Gold divider — vertical on desktop, horizontal on mobile */}
            <div className="hidden md:flex flex-col items-center justify-center self-stretch px-4">
              <div className="flex-1 w-px bg-gradient-to-b from-transparent via-[#C9A96E]/40 to-transparent" />
              <span className="text-[#C9A96E] text-xs py-3">✦</span>
              <div className="flex-1 w-px bg-gradient-to-b from-transparent via-[#C9A96E]/40 to-transparent" />
            </div>
            <div className="md:hidden flex items-center justify-center gap-3 py-4">
              <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#C9A96E]/40 to-transparent" />
              <span className="text-[#C9A96E] text-xs">✦</span>
              <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#C9A96E]/40 to-transparent" />
            </div>

            {/* Vision */}
            <div className="text-center px-6 md:px-10 pt-0" data-testid="story-vision">
              {/* Icon */}
              <div className="flex justify-center mb-4">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#C9A96E" strokeWidth="1.5">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                </svg>
              </div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#C9A96E] mb-4">Our Vision</p>
              <p className="cormorant text-xl sm:text-2xl font-light text-zinc-800" style={{ lineHeight: "1.75" }}>
                To become India's most trusted destination for modern sterling silver jewellery, inspiring women to celebrate every moment beautifully.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* ── CRAFT SECTION — full-width image with caption ────────────────── */}
      <section
        ref={craftRef}
        className="bg-white section-hidden"
        data-testid="story-craft"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 sm:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">

            {/* Image first on this section — right side */}
            <div className="order-2 lg:order-1">
              <div className="relative">
                <img
                  src={CRAFT_IMAGE}
                  alt="Silver bracelet craftsmanship detail"
                  className="w-full aspect-[4/3] object-cover"
                />
                {/* Floating label */}
                <div className="absolute bottom-4 left-4 bg-zinc-900/90 text-white px-4 py-2">
                  <p className="text-xs uppercase tracking-widest">Handcrafted in Jaipur</p>
                </div>
              </div>
            </div>

            {/* Text */}
            <div className="order-1 lg:order-2">
              <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-4">
                The Craft
              </p>
              <h2 className="cormorant text-4xl sm:text-5xl font-light text-zinc-900 leading-tight mb-8">
                Made by hands.<br />Worn with heart.
              </h2>
              <div className="space-y-5 text-zinc-600 leading-relaxed text-base">
                <p>
                  Jaipur is not just our home — it's one of the world's oldest jewellery-making
                  centres. The artisans here carry knowledge passed down across generations, and
                  every Rivora piece is shaped by that heritage.
                </p>
                <p>
                  We source only 92.5 hallmarked silver — the international standard for sterling
                  silver. Each piece is hand-finished, quality-checked, and packed with care before
                  it reaches you.
                </p>
                <p>
                  This isn't fast jewellery. It's made to be worn for years, gifted with meaning,
                  and kept close.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── WHY RIVORA — deeper pillars ──────────────────────────────────── */}
      <section
        ref={whyRef}
        className="bg-[#FAF7F2] py-20 sm:py-28 section-hidden"
        data-testid="story-why-rivora"
      >
        <div className="px-6 md:px-12 max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-4">What We Stand For</p>
            <h2
              className="cormorant text-4xl sm:text-5xl font-light text-zinc-900"
              data-testid="why-rivora-heading"
            >
              Why Rivora
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-12">
            {WHY_RIVORA.map(({ icon: Icon, title, description }, idx) => (
              <div
                key={title}
                className="flex flex-col group cursor-default"
                data-testid={`why-rivora-item-${title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {/* Oversized background number */}
                <span
                  className="cormorant font-light leading-none select-none mb-2 transition-opacity duration-300 group-hover:opacity-15"
                  style={{ fontSize: "5rem", color: "#1a1a1a", opacity: 0.08 }}
                >
                  0{idx + 1}
                </span>

                {/* Icon — lifts on hover */}
                <div className="mb-5 -mt-4 transition-transform duration-300 group-hover:-translate-y-1">
                  <Icon size={32} className="text-[#D4AF37] transition-colors duration-300" aria-hidden="true" />
                </div>

                {/* Heading — turns gold on hover */}
                <h3 className="cormorant text-2xl font-light text-zinc-900 mb-4 transition-colors duration-300 group-hover:text-[#C9A96E]">
                  {title}
                </h3>

                <p className="text-sm text-zinc-600" style={{ lineHeight: "1.8" }}>{description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FOUNDER NOTE — human element ─────────────────────────────────── */}
      <section
        ref={founderRef}
        className="bg-white py-20 sm:py-28 section-hidden"
        data-testid="story-founder"
      >
        <div className="px-6 md:px-12 max-w-3xl mx-auto text-center">
          {/* Decorative quote mark */}
          <p className="cormorant text-8xl text-[#D4AF37] leading-none select-none mb-0" aria-hidden="true">
            "
          </p>
          <blockquote className="cormorant text-2xl sm:text-3xl font-light italic text-zinc-800 leading-relaxed -mt-4">
            I wanted to build something real — not just a jewellery brand, but a daily ritual of
            beauty that every woman could afford. Rivora is that promise, kept in silver.
          </blockquote>
          <div className="mt-8">
            {/* Replace with your actual founder photo if available */}
            <div className="w-14 h-0.5 bg-[#D4AF37] mx-auto mb-5" aria-hidden="true" />
            <p className="cormorant text-xl font-light text-zinc-900">Founder, Rivora Jewels</p>
            <p className="text-xs uppercase tracking-widest text-zinc-400 mt-1">Jaipur, India</p>
          </div>
        </div>
      </section>

      {/* ── CLOSING CTA ──────────────────────────────────────────────────── */}
      <section
        ref={ctaRef}
        className="relative py-24 sm:py-32 text-center overflow-hidden section-hidden"
        data-testid="story-closing"
      >
        {/* Subtle background */}
        <div className="absolute inset-0 bg-zinc-900" />
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              "radial-gradient(circle at 30% 50%, #D4AF37 0%, transparent 60%), radial-gradient(circle at 70% 50%, #D4AF37 0%, transparent 60%)",
          }}
          aria-hidden="true"
        />

        <div className="relative z-10 px-6 md:px-12 max-w-4xl mx-auto">
          <p className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] mb-5">
            YOUR STORY BEGINS HERE
          </p>
          <p
            className="cormorant text-4xl sm:text-5xl lg:text-6xl font-light italic text-white leading-tight mb-10"
            data-testid="closing-text"
          >
            Wear silver you'll never want to take off.
          </p>
          <Link
            to="/shop"
            className="inline-flex items-center gap-3 bg-[#D4AF37] hover:bg-[#c49d2f] text-zinc-900 px-10 py-[1.1rem] uppercase tracking-[0.2em] text-xs font-medium transition-colors"
            data-testid="story-shop-cta"
          >
            Explore the Collection
            <ArrowRight size={14} />
          </Link>
          <p className="text-xs text-white/40 mt-6 uppercase tracking-widest">
            Free Shipping Across India • Hallmarked Silver • Secure Payments
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}